import { locationRoute, successRoute } from "../../App";
import { useNavigate } from "@tanstack/react-router";
import { useOrder } from "../../hooks/useOrder";
import { useCartStore } from "../../store/CartStore";
import { useEffect, useMemo } from "react";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { Footer } from "@/components/Footer";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import OrderConfirmation from "@/components/v0/order-confirmation";
import { OrderError } from "@/components/OrderError";
import { OrderLoading } from "@/components/OrderLoading";
import { getLocationConfig } from "@/providers/LocationConfigurationProvider";

export function SuccessPage() {
  const { locationId, paymentId } = successRoute.useParams();
  const { removeAll, price } = useCartStore();
  const navigate = useNavigate();
  const tracking = useTrackingEvent();

  const {
    data: order,
    isPending,
    refetch,
  } = useOrder({
    locationId,
    paymentId,
  });

  const { data: locationInfo } = useLocationInfo(locationId);

  useEffect(() => {
    if (order?.status === "COMPLETED") {
      try {
        tracking.capture("checkout_completed", {
          locationId,
          value: price,
        });
      } catch (error) {
        console.error("Error capturing checkout_completed event:", error);
      }
      removeAll();
    }
  }, [order?.status]);

  const state: "LOADING" | "SUCCESS" | "ERROR" = useMemo(() => {
    if (order !== undefined) {
      return "SUCCESS";
    }

    if (isPending) {
      return "LOADING";
    }
    return "ERROR";
  }, [isPending, order]);

  return (
    <>
      <ThemeProvider />
      <main className="min-h-screen bg-muted flex flex-col items-center justify-center p-4">
        {state === "LOADING" && <OrderLoading />}

        {state === "SUCCESS" && order && (
          <OrderConfirmation
            order={order}
            location={{
              name: locationInfo?.name ?? "",
              cvr: locationInfo?.cvr ?? "",
              id: locationId,
            }}
            useShortOrderNumber={getLocationConfig(locationId).useShortOrderNumber ?? false}
            onRefresh={refetch}
            goToMenu={() => {
              navigate({
                to: locationRoute.to,
                params: { locationId },
                resetScroll: true,
              });

              // Reset scroll doesn't work, so this is a safe-guard
              window.scrollTo({
                top: 0,
              });
            }}
          />
        )}

        {state === "ERROR" && (
          <OrderError paymentId={paymentId} locationId={locationId} />
        )}

        <Footer locationId={locationId} className="bg-transparent w-full" />
      </main>
    </>
  );
}
